import { memo, useCallback, useEffect, useState } from "react";
import { GoogleMap, Polyline, useJsApiLoader } from "@react-google-maps/api";
import { useGeolocated } from "react-geolocated";
import { GOOGLE_MAP_API_KEY } from "../../../../utils/constants";
import { isNil } from "lodash";

const containerStyle = {
  width: "100%",
  height: "320px",
};

const center = {
  lat: -3.745,
  lng: -38.523,
};

interface IProps {
  destination: {
    lat: number;
    lng: number;
  } | null;
  origin: {
    lat: number;
    lng: number;
  } | null;
}

const MapComponent = ({ destination, origin }: IProps) => {
  const { isGeolocationAvailable, isGeolocationEnabled, coords } =
    useGeolocated({
      positionOptions: {
        enableHighAccuracy: false,
      },
      userDecisionTimeout: 5000,
    });

  const [center, setCenter] = useState({ lat: -3.745, lng: -38.523 });
  const [zoom, setZoom] = useState(12);

  const { isLoaded } = useJsApiLoader({
    id: "google-map-script",
    googleMapsApiKey: GOOGLE_MAP_API_KEY,
  });

  const [map, setMap] = useState(null);

  useEffect(() => {
    if (isLoaded && coords) {
      setCenter({
        lat: coords.latitude,
        lng: coords.longitude,
      });
    }
  }, [coords]);

  useEffect(() => {
    if (!isNil(origin)) {
      setCenter(origin);
      setZoom(11);
    }
  }, [origin]);

  useEffect(() => {
    if (!isNil(origin) && !isNil(destination)) {
      setZoom(9);
    }
  }, [origin, destination]);

  const onLoad = useCallback(function callback(map: any) {
    const bounds = new window.google.maps.LatLngBounds(center);
    map.fitBounds(bounds);
    setMap(map);
  }, []);

  const onUnmount = useCallback(function callback(map: any) {
    setMap(null);
  }, []);

  return isLoaded ? (
    <GoogleMap
      mapContainerStyle={containerStyle}
      center={center}
      zoom={zoom}
      onLoad={onLoad}
      onUnmount={onUnmount}
    >
      {isNil(origin) || isNil(destination) ? null : (
        <Polyline path={[origin, destination]} />
      )}
    </GoogleMap>
  ) : (
    <></>
  );
};

export default memo(MapComponent);
